/**
 *
 * Routing concept here:
 *
 */
import sablonoRootHtml from "./sablono.html";
import projectHtml from "./states/project.html";
import PresentableError from "../../errors/PresentableError";
import ParseableError from "../../errors/ParseableError";
import AuthenticationError from "../../errors/AuthenticationError";

// keep in sync with the projectId regex used in the sablono.project state url below
const PROJECT_ID_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export default function ($stateProvider) {
    "ngInject";
    // THE ROOT SABLONO STATE
    //
    $stateProvider.state("sablono", {
        abstract: true,
        templateUrl: sablonoRootHtml,
        controller: "SablonoCtrl as sablono",
        data: {
            isSubBarHidden: true,
        },
        resolve: {
            session: function (
                $sbAuth,
                intercomService,
                $sbFeatureDecisions,
                $sbExternalService,
                Analytics,
                $q,
                $sbUsersApi
            ) {
                "ngInject";
                return $q
                    .all([$sbAuth.getCurrentUser(), $sbUsersApi.createToken()])
                    .then(([user, jwtToken]) => {
                        // call intercom boot in async manner -> should not affect our service
                        intercomService.boot(jwtToken.access_token);

                        $sbExternalService.setIntercomService(intercomService);
                        $sbFeatureDecisions.updateContext({ user });

                        Analytics.trackIdentity(user.id);
                        return user;
                    });
            },
        },
    });

    // the project state -> so everything that is related to a project is child of this state
    //
    $stateProvider.state("sablono.project", {
        //[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}
        abstract: true,
        url: "/{projectId:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}}",
        templateUrl: projectHtml,
        controller: "ProjectCtrl as project",
        data: {
            isSubBarHidden: false,
        },
        resolve: {
            project: function resolveProjectAndPrivileges(
                $q,
                $stateParams,
                $sbProjectApi,
                $sbCurrentProject,
                $sbProjectCurrentSettings,
                $sbDetailsOverlayData,
                $sbAuthStore,
                $sbMembership
            ) {
                "ngInject";

                if (!PROJECT_ID_PATTERN.test($stateParams.projectId)) {
                    // guards against stale/malformed redirects that reach this
                    // state without a valid projectId, e.g. a stored post-login target
                    return $q.reject(
                        new PresentableError(
                            "MissingProjectId",
                            "ERROR_MISSING_PROJECT_ID_TITLE",
                            "ERROR_MISSING_PROJECT_ID_MESSAGE"
                        )
                    );
                }

                return $q
                    .resolve($sbAuthStore.getLastSuccessfulSignInIdentity())
                    .then((user) => {
                        if (!user) {
                            return $q.reject(new AuthenticationError());
                        }
                        return $sbMembership
                            .get($stateParams.projectId, user.userId)
                            .catch((error) => {
                                // Membership requests failed with 404 indicating that
                                // the user is not a member of the project.
                                //
                                if (
                                    error instanceof ParseableError &&
                                    error.code === "REQUEST" &&
                                    error.request.status === 404
                                ) {
                                    const err = new PresentableError(
                                        "MissingProjectMembership",
                                        "ERROR_MISSING_PROJECT_MEMBERSHIP_TITLE",
                                        "ERROR_MISSING_PROJECT_MEMBERSHIP_MESSAGE"
                                    );
                                    err.cause = error;
                                    return $q.reject(err);
                                }
                                return $q.reject(error);
                            });
                    })
                    .then(() => {
                        return $sbProjectApi
                            .get($stateParams.projectId)
                            .then($sbCurrentProject.set)
                            .then((project) => {
                                $sbDetailsOverlayData.setCurrentProject(
                                    project
                                );
                                $sbProjectCurrentSettings.clear();
                                return project;
                            });
                    });
            },
        },
    });
}
